import { loadStripe } from '@stripe/stripe-js';
import { supabase } from './supabase';

if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_STRIPE_PUBLISHABLE_KEY');
}

export const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export async function createCheckoutSession(priceId: string, mode: 'payment' | 'subscription', accountId: string, includeTrial: boolean = true) {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      window.location.href = `/auth?redirect=${encodeURIComponent(window.location.pathname)}`;
      return;
    }

    if (!priceId) {
      throw new Error('Invalid price ID provided');
    }

    console.log('Creating checkout session with:', { priceId, mode, accountId });

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        price_id: priceId,
        mode,
        success_url: `${window.location.origin}/`,
        cancel_url: `${window.location.origin}/`,
        allow_promotion_codes: true,
        automatic_tax: true,
        tax_id_collection: true,
        account_id: accountId,
        include_trial: includeTrial
      }),
    });

    if (!response.ok) {
      const responseData = await response.json();
      throw new Error(responseData.error || responseData.details || 'Failed to create checkout session');
    }

    const { url } = await response.json();
    if (!url) {
      throw new Error('No checkout URL returned');
    }

    window.location.href = url;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
}

export async function updateSubscription(subscriptionId: string, priceId: string) {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session?.access_token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-update`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription_id: subscriptionId,
        price_id: priceId
      }),
    });

    if (!response.ok) {
      const responseData = await response.json();
      throw new Error(responseData.error || 'Failed to update subscription');
    }

    return await response.json();
    
    // Note: We don't track free trial for subscription updates
    // as they are existing customers
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw error;
  }
}